import React from 'react';
import { Download, Eye, AlertCircle, Loader2 } from 'lucide-react';
import { CHAT_TOKENS } from './chatTokens';

/**
 * AttachmentCard — neutral white card rendered INSIDE a message
 * bubble (per the mint redesign spec) or below it, always with the
 * same visual. Reused by admin + customer sides and by legacy
 * tickets. The bubble tint does not bleed into this card.
 *
 * See /tmp/TZ2/ticket-chat-redesign-spec.md §78-110.
 *
 *   props:
 *     filename        display label (up to 2 lines with ellipsis)
 *     fullTitle       full name for tooltip
 *     size            bytes
 *     ext             file extension without leading dot
 *     uploadedAt      ISO timestamp
 *     onDownload      required handler (whole card also triggers this)
 *     onPreviewPdf    optional — adds Eye button for PDFs
 *     onCopyPath      legacy-only — copy stored path to clipboard
 *     legacy          true for legacy attachment rows
 *     downloadCount   integer OR null (null hides the badge)
 *     imageUrl        thumbnail URL for image attachments (replaces
 *                     the type badge with a 36×36 image)
 *     onOpenImage     click handler for the thumbnail (lightbox)
 *     downloadDisabled  disable Download button (in-flight state)
 *     downloadProgress  0..1 shown as an overlay when defined
 *     testid
 */

// Extension → visual bucket. Colours match tokens in CHAT_TOKENS so a
// designer edits one file to re-theme the whole chat.
const bucketFor = (extRaw) => {
  const ext = (extRaw || '').toLowerCase().replace(/^\./, '');
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return { label: ext.toUpperCase(), bg: CHAT_TOKENS.iconZipBg, fg: CHAT_TOKENS.iconZipText };
  if (ext === 'pdf')                                    return { label: 'PDF', bg: CHAT_TOKENS.iconPdfBg, fg: CHAT_TOKENS.iconPdfText };
  if (['jpg','jpeg','png','gif','webp','bmp','heic'].includes(ext))
                                                        return { label: ext.toUpperCase(), bg: '#e0f2fe', fg: '#075985' };
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext))     return { label: ext.toUpperCase(), bg: '#eff6ff', fg: '#1e3a8a' };
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext))     return { label: ext.toUpperCase(), bg: '#ecfdf5', fg: '#065f46' };
  if (ext) return { label: ext.slice(0, 4).toUpperCase(), bg: CHAT_TOKENS.iconNeutralBg, fg: CHAT_TOKENS.iconNeutralText };
  return { label: 'FILE', bg: CHAT_TOKENS.iconNeutralBg, fg: CHAT_TOKENS.iconNeutralText };
};

const pad = (n) => String(n).padStart(2, '0');
const fmtStamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatBytes = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function AttachmentCard({
  filename,
  fullTitle,
  size,
  ext,
  uploadedAt,
  onDownload,
  onPreviewPdf,
  onCopyPath,
  legacy,
  downloadCount,
  imageUrl,
  onOpenImage,
  downloadDisabled,
  downloadProgress,
  testid,
}) {
  const bucket = bucketFor(ext);
  const displayName = filename || 'Attachment';
  const tooltip = fullTitle || displayName;

  const showBadge = downloadCount !== null && downloadCount !== undefined;
  const isDownloaded = (downloadCount || 0) > 0;

  const handleCardClick = (e) => {
    if (e.target.closest('button')) return;
    if (downloadDisabled) return;
    onDownload && onDownload();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !downloadDisabled) {
          e.preventDefault();
          onDownload && onDownload();
        }
      }}
      className={`mt-1.5 grid gap-2.5 rounded-[10px] transition-colors ${downloadDisabled ? 'opacity-70 cursor-progress' : 'cursor-pointer hover:border-slate-300'}`}
      style={{
        gridTemplateColumns: '36px 1fr auto',
        alignItems: 'center',
        padding: '8px 10px',
        background: CHAT_TOKENS.attBg,
        border: `1px solid ${CHAT_TOKENS.attBorder}`,
      }}
      data-testid={testid}
      title={tooltip}
    >
      {/* ── Type badge / thumbnail (36×36) ─────────────────── */}
      <div>
        {imageUrl ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenImage && onOpenImage(); }}
            className="w-9 h-9 rounded-[7px] overflow-hidden border"
            style={{ borderColor: CHAT_TOKENS.attBorder, background: '#f1f5f9' }}
            data-testid={testid ? `${testid}-thumb` : undefined}
            title={onOpenImage ? 'Open preview' : tooltip}
          >
            <img src={imageUrl} alt="" className="w-full h-full object-cover" />
          </button>
        ) : (
          <div
            className="w-9 h-9 rounded-[7px] flex items-center justify-center font-bold tracking-wider"
            style={{ background: bucket.bg, color: bucket.fg, fontSize: 10 }}
          >
            {bucket.label}
          </div>
        )}
      </div>

      {/* ── Body: name + meta ─────────────────────────────── */}
      <div className="min-w-0 flex flex-col">
        {onCopyPath ? (
          <code
            className="font-mono cursor-pointer hover:underline leading-[1.35]"
            style={{ fontSize: 12.5, fontWeight: 500, color: CHAT_TOKENS.text, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-all' }}
            title={tooltip}
            onClick={(e) => { e.stopPropagation(); onCopyPath(); }}
          >
            {displayName}
          </code>
        ) : (
          <span
            className="leading-[1.35]"
            style={{ fontSize: 12.5, fontWeight: 500, color: CHAT_TOKENS.text, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}
            title={tooltip}
          >
            {displayName}
          </span>
        )}
        <div
          className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-0.5"
          style={{ fontSize: 10.5, color: CHAT_TOKENS.meta }}
        >
          {size != null && <span className="font-mono">{formatBytes(size)}</span>}
          {size != null && uploadedAt && <span aria-hidden style={{ width: 2, height: 2, borderRadius: 999, background: '#cbd5e1' }} />}
          {uploadedAt && <span className="font-mono">{fmtStamp(uploadedAt)}</span>}
          {legacy && (
            <>
              <span aria-hidden style={{ width: 2, height: 2, borderRadius: 999, background: '#cbd5e1' }} />
              <span className="uppercase font-semibold tracking-wider">Legacy</span>
            </>
          )}
          {showBadge && (
            <>
              <span aria-hidden style={{ width: 2, height: 2, borderRadius: 999, background: '#cbd5e1' }} />
              {isDownloaded ? (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-semibold"
                  style={{ background: CHAT_TOKENS.badgeOkBg, color: CHAT_TOKENS.badgeOkText }}
                  data-testid={testid ? `${testid}-download-badge` : undefined}
                  data-state="downloaded"
                  title={`Downloaded ${downloadCount} time${downloadCount === 1 ? '' : 's'}`}
                >
                  <Download className="w-3 h-3" />
                  {downloadCount} download{downloadCount === 1 ? '' : 's'}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-semibold"
                  style={{ background: CHAT_TOKENS.badgeWarnBg, color: CHAT_TOKENS.badgeWarnText }}
                  data-testid={testid ? `${testid}-download-badge` : undefined}
                  data-state="not-downloaded"
                  title="Recipient has not downloaded this file yet"
                >
                  <AlertCircle className="w-3 h-3" />
                  not downloaded
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Action buttons ─────────────────────────────────── */}
      <div className="flex items-center gap-1">
        {onPreviewPdf && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPreviewPdf(); }}
            title="Preview PDF"
            data-testid={testid ? `${testid}-preview` : undefined}
            className="w-[30px] h-[30px] rounded-[7px] flex items-center justify-center hover:bg-slate-200"
            style={{ background: CHAT_TOKENS.iconNeutralBg, color: CHAT_TOKENS.iconPdfText }}
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDownload && onDownload(); }}
          disabled={downloadDisabled}
          title="Download"
          data-testid={testid ? `${testid}-download` : undefined}
          className="w-[30px] h-[30px] rounded-[7px] flex items-center justify-center hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: CHAT_TOKENS.iconNeutralBg, color: CHAT_TOKENS.iconNeutralText }}
        >
          {downloadDisabled ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* In-flight progress bar overlay (spec §108) */}
      {downloadProgress != null && (
        <div style={{ gridColumn: '1 / -1', height: 3, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ width: `${Math.round(downloadProgress * 100)}%`, height: '100%', background: CHAT_TOKENS.readActive, transition: 'width 200ms linear' }} />
        </div>
      )}
    </div>
  );
}
